import { useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import { isAfter } from "date-fns";

import * as requestUrl from "constants/request";
import noAuthUrls from "constants/noAuthUrls";
import { useParams } from "react-router-dom";
import { FIRSTECH_MFA_ENABLED } from "constants/idp-claims";
import { FT_TOKEN_KEY } from "constants/session";

export interface GuestAccessTokenResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: string;
  validTo: string;
}

const requestUrls: { [key: string]: number } = Object.create(null, {});

async function fetchToken(attempt?: number): Promise<GuestAccessTokenResponse> {
  const result: AxiosResponse<GuestAccessTokenResponse> =
    await axios.get<GuestAccessTokenResponse>(requestUrl.FT_GET_TOKEN, {
      baseURL: requestUrl.FT_REQUEST_BASE_URL,
    });

  if (isAfter(new Date(result.data.validTo), new Date())) {
    sessionStorage.setItem(FT_TOKEN_KEY, JSON.stringify(result.data));
    return result.data;
  } else if (!!attempt && attempt > 2) {
    return result.data;
  } else {
    return await fetchToken((attempt ?? 0) + 1);
  }
}

function loadTokenFromLocalStorage(): string | null {
  /*
   * Grab the token response from local storage and parse it.
   * Use the accessToken property for the token.
   */
  const tokenStr = sessionStorage.getItem(FT_TOKEN_KEY);
  let token = null;

  if (!!tokenStr) {
    let accessTokenResponse = null;
    try {
      accessTokenResponse = JSON.parse(tokenStr) as GuestAccessTokenResponse;
    } catch (e) {
      console.error("Unable to parse the access token info", e);
    }

    if (!!accessTokenResponse) {
      const expires = new Date(accessTokenResponse.validTo);

      if (isAfter(expires, new Date())) {
        token = accessTokenResponse.accessToken;
      }
    }
  }

  return token;
}

async function getToken(requestUrl?: string): Promise<string | null> {
  const match = noAuthUrls.filter((single) => {
    return single === requestUrl;
  });

  let token = null;

  if (match.length === 0) {
    // Try to get the token from local storage.
    token = loadTokenFromLocalStorage();

    // Got no token? Fetch it
    if (!token) {
      const tokenResponse = await fetchToken();
      // If we got a response grab it from the accessToken property.
      if (!!tokenResponse) {
        token = tokenResponse.accessToken;
      }
    }
  }

  return token;
}

export const useRequest = (scope?: string) => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    getAccessTokenWithPopup,
    getIdTokenClaims,
  } = useAuth0();
  const { customerKey } = useParams();

  const request: AxiosInstance = useMemo(() => {
    const reqObj = axios.create({
      baseURL: requestUrl.FT_REQUEST_BASE_URL,
    });

    reqObj.interceptors.request.use(
      async function (config) {
        const abortController = new AbortController();
        config.signal = abortController.signal;

        let token = null;
        if (!isAuthenticated) {
          token = await getToken(config.url);
        } else {
          const idtokenclaims = await getIdTokenClaims();
          if (!!idtokenclaims && !!idtokenclaims[FIRSTECH_MFA_ENABLED]) {
            token = !!scope
              ? await getAccessTokenWithPopup({
                  authorizationParams: { scope },
                })
              : await getAccessTokenSilently();
          } else {
            token = !!scope
              ? await getAccessTokenSilently({ authorizationParams: { scope } })
              : await getAccessTokenSilently();
          }
          // try {
          //   token = !!scope
          //     ? await getAccessTokenSilently({ authorizationParams: { scope } })
          //     : await getAccessTokenSilently();
          // } catch (e) {
          //   console.log("oh no!", e);
          // }
        }

        const url: string | undefined = config.url
          ?.replace(
            "FT_CLIENT_KEY",
            customerKey ? customerKey.toLocaleLowerCase() : "",
          )
          .trim();
        let newConfig: { url?: string; headers?: any } = {
          url,
          headers: { ...config.headers },
        };

        if (!!token) {
          newConfig.headers.Authorization = `Bearer ${token}`;
        }

        if (url) {
          if (requestUrls[url]) {
            abortController.abort();
          }
          requestUrls[url] = requestUrls[url] ? requestUrls[url] + 1 : 1;
        }

        return { ...config, ...newConfig };
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      },
    );

    // Add a request interceptor
    // Add a response interceptor
    reqObj.interceptors.response.use(
      function (response) {
        if (response.config.url) {
          if (requestUrls[response.config.url] === 1) {
            delete requestUrls[response.config.url];
          } else {
            requestUrls[response.config.url] =
              requestUrls[response.config.url] - 1;
          }
        }

        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
      },
      function (error) {
        if (error.config?.url) {
          if (requestUrls[error.config.url] === 1) {
            delete requestUrls[error.config.url];
          } else {
            requestUrls[error.config.url] = requestUrls[error.config.url] - 1;
          }
        }

        console.log("errorerrorerrorerrorerrorerror", error);

        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        return Promise.reject(error);
      },
    );

    return reqObj;
  }, [
    getAccessTokenSilently,
    getIdTokenClaims,
    getAccessTokenWithPopup,
    isAuthenticated,
    scope,
    customerKey,
  ]);

  return {
    request,
  };
};

export default useRequest;
